import { useEffect, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Modal } from '@shared/ui/Modal';
import { Table } from '@shared/ui/Table';
import { Skeleton } from '@shared/ui/Skeleton';
import { Field, Input, Label, Select } from '@shared/ui/Input';
import { RegistryEmpty } from '@shared/ui/RegistryShell';
import {
  useCreateKadryWorkSiteMutation,
  useListKadryWorkSitesQuery,
  useUpdateKadryWorkSiteMutation,
} from '@shared/api';
import {
  KADRY_RU_TIMEZONES,
  kadryDate,
  kadryTimezoneLabel,
  useKadryAccess,
  type KadryWorkSite,
  type KadryWorkSiteInput,
} from '@entities/kadry';
import {
  KadryBannerGap,
  KadryCheckbox,
  KadryErrorBanner,
  KadryFieldError,
  KadryFieldGrid,
  KadryFieldHint,
  KadryFormActions,
  KadryMono,
  KadryMuted,
  KadryTableScroll,
} from '../ui';

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const NameCell = styled.td`
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const Wrap = styled.td`
  max-width: 280px;
  overflow-wrap: anywhere;
`;

const Nowrap = styled.td`
  white-space: nowrap;
`;

const DEFAULT_TZ = 'Europe/Moscow';
const REGION_RE = /^[A-Za-z0-9-]{1,8}$/;

interface FormState {
  name: string;
  address: string;
  regionCode: string;
  timezone: string;
  active: boolean;
}

function toForm(s?: KadryWorkSite): FormState {
  return {
    name: s?.name ?? '',
    address: s?.address ?? '',
    regionCode: s?.region_code ?? '',
    timezone: s?.timezone ?? DEFAULT_TZ,
    active: s?.active ?? true,
  };
}

/** Справочник объектов: место выполнения работ и его часовой пояс. */
export function WorkSites() {
  const [showInactive, setShowInactive] = useState(false);
  const { data, isLoading, isFetching, error, refetch } = useListKadryWorkSitesQuery({
    include_inactive: showInactive,
  });
  const [editing, setEditing] = useState<KadryWorkSite | 'new' | null>(null);
  // В режиме «только чтение» справочник виден, но не меняется.
  const editable = useKadryAccess().canWrite('admin_settings');
  const cols = editable ? 7 : 6;
  const items = data ?? [];

  return (
    <>
      <Toolbar>
        <KadryCheckbox checked={showInactive} onChange={setShowInactive}>
          Показывать выведенные из справочника
        </KadryCheckbox>
        {editable && (
          <Button type="button" onClick={() => setEditing('new')}>
            + Объект
          </Button>
        )}
      </Toolbar>

      {error ? (
        <KadryBannerGap>
          <KadryErrorBanner error={error} onRefresh={refetch} title="Не удалось загрузить объекты" />
        </KadryBannerGap>
      ) : null}

      <Card>
        <KadryTableScroll>
          <Table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Адрес</th>
                <th>Регион</th>
                <th>Часовой пояс</th>
                <th>Статус</th>
                <th>Изменён</th>
                {editable && <th aria-label="Действия" />}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: cols }).map((__, j) => (
                      <td key={j}>
                        <Skeleton $width={j === 0 ? '80%' : 80} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <RegistryEmpty colSpan={cols}>
                    Объектов пока нет. Добавьте места выполнения работ — их выбирают в заявках.
                  </RegistryEmpty>
                </tr>
              ) : (
                items.map((s) => (
                  <tr key={s.id}>
                    <NameCell>{s.name}</NameCell>
                    <Wrap>{s.address ?? '—'}</Wrap>
                    <td>{s.region_code ? <KadryMono>{s.region_code}</KadryMono> : '—'}</td>
                    <Nowrap>{kadryTimezoneLabel(s.timezone)}</Nowrap>
                    <td>
                      {s.active ? (
                        <Badge $tone="success">Активен</Badge>
                      ) : (
                        <Badge>Выведен</Badge>
                      )}
                    </td>
                    <Nowrap>{kadryDate(s.updated_at)}</Nowrap>
                    {editable && (
                      <td>
                        <Button type="button" $size="sm" $variant="ghost" onClick={() => setEditing(s)}>
                          Изменить
                        </Button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </KadryTableScroll>
      </Card>
      {isFetching && !isLoading && <KadryMuted>Обновление…</KadryMuted>}

      <WorkSiteModal
        site={editing === 'new' ? undefined : (editing ?? undefined)}
        open={editing != null}
        onClose={() => setEditing(null)}
        onRefresh={refetch}
      />
    </>
  );
}

interface ModalProps {
  open: boolean;
  site?: KadryWorkSite;
  onClose: () => void;
  onRefresh: () => void;
}

function WorkSiteModal({ open, site, onClose, onRefresh }: ModalProps) {
  const [f, setF] = useState<FormState>(() => toForm(site));
  const [touched, setTouched] = useState(false);
  const [create, createState] = useCreateKadryWorkSiteMutation();
  const [update, updateState] = useUpdateKadryWorkSiteMutation();
  const busy = createState.isLoading || updateState.isLoading;
  const error = createState.error ?? updateState.error;

  useEffect(() => {
    if (open) {
      setF(toForm(site));
      setTouched(false);
      createState.reset();
      updateState.reset();
    }
    // Сброс только при открытии формы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, site?.id]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((p) => ({ ...p, [k]: v }));

  const errors = {
    name: !f.name.trim() || f.name.trim().length > 200 ? 'Название: от 1 до 200 символов' : null,
    address: f.address.length > 500 ? 'Адрес: не длиннее 500 символов' : null,
    regionCode:
      f.regionCode.trim() && !REGION_RE.test(f.regionCode.trim())
        ? 'Код региона: до 8 символов — латиница, цифры, дефис (например, 66 или RU-SVE)'
        : null,
  };
  const invalid = Object.values(errors).some(Boolean);
  // Зона вне списка (заведена раньше или через API) остаётся доступной для выбора.
  const knownTz = KADRY_RU_TIMEZONES.some((t) => t.tz === f.timezone);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (invalid || busy) return;
    const body: KadryWorkSiteInput = {
      name: f.name.trim(),
      address: f.address.trim() || null,
      region_code: f.regionCode.trim() || null,
      timezone: f.timezone,
      // PUT заменяет объект целиком — связь с объектом «Полевого» сохраняем как есть.
      field_site_id: site?.field_site_id ?? null,
      active: f.active,
    };
    try {
      if (site) {
        await update({ id: site.id, version: site.version, body }).unwrap();
      } else {
        await create(body).unwrap();
      }
      onClose();
    } catch {
      // Ошибка показывается в форме.
    }
  }

  return (
    <Modal open={open} title={site ? 'Изменить объект' : 'Новый объект'} onClose={() => !busy && onClose()}>
      <form onSubmit={submit} noValidate>
        <Field>
          <Label htmlFor="ws-name">Название *</Label>
          <Input
            id="ws-name"
            value={f.name}
            maxLength={200}
            placeholder="Например: Площадка «Северный промузел»"
            onChange={(e) => set('name', e.target.value)}
            autoFocus
          />
          {touched && errors.name && <KadryFieldError>{errors.name}</KadryFieldError>}
        </Field>
        <Field>
          <Label htmlFor="ws-address">Адрес</Label>
          <Input
            id="ws-address"
            value={f.address}
            maxLength={500}
            onChange={(e) => set('address', e.target.value)}
          />
          {touched && errors.address && <KadryFieldError>{errors.address}</KadryFieldError>}
        </Field>
        <KadryFieldGrid>
          <Field>
            <Label htmlFor="ws-region">Код региона</Label>
            <Input
              id="ws-region"
              value={f.regionCode}
              maxLength={8}
              placeholder="66"
              onChange={(e) => set('regionCode', e.target.value)}
            />
            {touched && errors.regionCode ? (
              <KadryFieldError>{errors.regionCode}</KadryFieldError>
            ) : (
              <KadryFieldHint>Код субъекта РФ; сохраняется в верхнем регистре.</KadryFieldHint>
            )}
          </Field>
          <Field>
            <Label htmlFor="ws-tz">Часовой пояс *</Label>
            <Select id="ws-tz" value={f.timezone} onChange={(e) => set('timezone', e.target.value)}>
              {!knownTz && <option value={f.timezone}>{f.timezone}</option>}
              {KADRY_RU_TIMEZONES.map((t) => (
                <option key={t.tz} value={t.tz}>
                  {kadryTimezoneLabel(t.tz)}
                </option>
              ))}
            </Select>
            <KadryFieldHint>
              По местному времени объекта бот соблюдает тихие часы и считает суточные лимиты.
            </KadryFieldHint>
          </Field>
        </KadryFieldGrid>
        <Field>
          <KadryCheckbox
            checked={f.active}
            onChange={(v) => set('active', v)}
            hint="Выведенный объект нельзя выбрать в новых заявках; в существующих он сохраняется."
          >
            Объект активен
          </KadryCheckbox>
        </Field>
        {error ? (
          <Field>
            <KadryErrorBanner
              error={error}
              onRefresh={() => {
                onRefresh();
                onClose();
              }}
              onDismiss={() => {
                createState.reset();
                updateState.reset();
              }}
            />
          </Field>
        ) : null}
        <KadryFormActions>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button type="submit" disabled={busy || (touched && invalid)}>
            {busy ? 'Сохраняем…' : site ? 'Сохранить' : 'Добавить объект'}
          </Button>
        </KadryFormActions>
      </form>
    </Modal>
  );
}
